import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import FareMarketBenchmarksEditor from '../components/FareMarketBenchmarksEditor';
import { C2, C2_FONT, C2TableCard } from '../components/Table/C2UserTable';

const RATE_LABELS = {
  base_fare: 'Base',
  min_fare: 'Mínima',
  rate_per_unit_distance: 'Distancia',
  rate_per_hour: 'Minuto (T)',
  convenience_fees: 'Comisión',
};

function formatRates(obj) {
  if (!obj) return '—';
  return Object.keys(RATE_LABELS)
    .filter((k) => obj[k] != null && obj[k] !== '')
    .map((k) => `${RATE_LABELS[k]} ${obj[k]}`)
    .join(' · ');
}

export default function FareRecommendations() {
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settingsdata.settings);
  const cartypes = useSelector((state) => state.cartypes?.cars || []);
  const { list, loading } = useSelector((state) => state.fareMarketAdvisor || { list: [], loading: true });
  const { fetchFareRecommendations, runFareMarketAdvisorNow, fetchCarTypes } = api;

  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [lastApplied, setLastApplied] = useState(null);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    dispatch(fetchFareRecommendations());
  }, [dispatch, fetchFareRecommendations]);

  const activeVehicles = useMemo(
    () => (cartypes || []).filter((c) => c.visibleInApp !== false).sort((a, b) => (a.pos || 0) - (b.pos || 0)),
    [cartypes]
  );

  const history = useMemo(
    () =>
      (list || [])
        .filter((r) => r.status === 'applied' && r.carTypes)
        .sort((a, b) => (b.appliedAt || b.createdAt || 0) - (a.appliedAt || a.createdAt || 0)),
    [list]
  );

  const onAnalyze = async () => {
    setMsg(null);
    setAnalysis(null);
    setBusy('analyze');
    const result = await runFareMarketAdvisorNow({ dryRun: true });
    setBusy(null);
    if (result.success) {
      if (result.skipped) {
        setMsg({ severity: 'warning', text: result.reason || 'Sin sugerencias.' });
      } else {
        setAnalysis(result);
        setMsg({
          severity: 'info',
          text: `Análisis listo (sin aplicar): ${result.summary?.cities || 0} ciudades · ${result.summary?.cityCarSuggestions || result.summary?.carTypes || 0} sugerencias. ${result.modelSummary || ''}`,
        });
      }
    } else {
      setMsg({ severity: 'error', text: result.error });
    }
  };

  const onRun = async () => {
    setMsg(null);
    setBusy('run');
    const result = await runFareMarketAdvisorNow();
    setBusy(null);
    if (result.success) {
      if (result.skipped) {
        const reason =
          result.reason === 'no_active_vehicles' || result.reason === 'no_pricing_cities'
            ? 'No hay vehículos/ciudades activos para calcular.'
            : result.reason === 'no_suggestions'
              ? 'DeepSeek no devolvió sugerencias útiles.'
              : 'No se generaron cambios.';
        setMsg({ severity: 'warning', text: reason });
      } else {
        setLastApplied(result.applied || null);
        setAnalysis(result);
        setMsg({
          severity: 'success',
          text: `Tarifas activadas: ${result.summary?.carTypes || 0} vehículos · ${result.summary?.cities || 0} ciudades. ${result.modelSummary || ''}`,
        });
        if (typeof fetchCarTypes === 'function') dispatch(fetchCarTypes());
      }
    } else {
      setMsg({ severity: 'error', text: result.error });
    }
  };

  if (loading && !(list || []).length && !activeVehicles.length) {
    return <CircularLoading />;
  }

  return (
    <Box sx={{ pb: 4 }}>
      <C2TableCard sx={{ mb: 2, p: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <AutoAwesomeOutlinedIcon sx={{ color: C2.violet, fontSize: 40 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, color: C2.sidebar, fontSize: '1.05rem' }}>
              Cálculo inteligente de tarifas (DeepSeek)
            </Typography>
            <Typography sx={{ fontFamily: C2_FONT, color: C2.muted, fontSize: '0.9rem', mt: 0.5 }}>
              Analiza por ciudad los vehículos activos, tus viajes y bandas de mercado Colombia (no scrape en vivo de Uber/DiDi). Opcional: carga referencias en RTDB fare_market_benchmarks.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={!!busy || settings.fareMarketAdvisorEnabled !== true}
              onClick={onAnalyze}
              sx={{ fontFamily: C2_FONT, textTransform: 'none' }}
            >
              {busy === 'analyze' ? 'Analizando…' : 'Solo analizar'}
            </Button>
            <Button
              variant="contained"
              startIcon={<PlayArrowOutlinedIcon />}
              disabled={!!busy || settings.fareMarketAdvisorEnabled !== true}
              onClick={onRun}
              sx={{ fontFamily: C2_FONT, textTransform: 'none', bgcolor: C2.violet }}
            >
              {busy === 'run' ? 'Calculando y activando…' : 'Calcular y activar'}
            </Button>
          </Stack>
        </Stack>

        {settings.fareMarketAdvisorEnabled !== true ? (
          <Alert severity="warning" sx={{ mt: 2, fontFamily: C2_FONT }}>
            Activa el interruptor y guarda la API key de DeepSeek en{' '}
            <RouterLink to="/settings">Configuración → Operación → Tarifas por ciudad</RouterLink>
            {' '}para habilitar los botones.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mt: 2, fontFamily: C2_FONT }}>
            Uber/DiDi no publican API de precios para terceros: no se puede cotizar en vivo sin scrapear (frágil y contra términos). Usamos bandas de referencia + tus viajes + benchmarks que tú cargues. “Solo analizar” no cambia tarifas.
          </Alert>
        )}

        {msg ? (
          <Alert severity={msg.severity} sx={{ mt: 2, fontFamily: C2_FONT }} onClose={() => setMsg(null)}>
            {msg.text}
          </Alert>
        ) : null}
      </C2TableCard>

      <FareMarketBenchmarksEditor />

      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, color: C2.sidebar, mb: 1 }}>
        Vehículos activos ahora ({activeVehicles.length})
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {activeVehicles.map((car) => (
          <Grid item xs={12} sm={6} md={4} key={car.id}>
            <C2TableCard sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                {car.image ? (
                  <Box component="img" src={car.image} alt="" sx={{ width: 48, height: 48, objectFit: 'contain' }} />
                ) : null}
                <Box>
                  <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, color: C2.sidebar }}>
                    {car.name}
                  </Typography>
                  <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted }}>
                    {formatRates({
                      min_fare: car.min_fare,
                      rate_per_unit_distance: car.rate_per_unit_distance,
                      rate_per_hour: car.rate_per_hour,
                    })}
                  </Typography>
                </Box>
              </Stack>
            </C2TableCard>
          </Grid>
        ))}
      </Grid>

      {analysis?.byCity ? (
        <>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, color: C2.sidebar, mb: 1 }}>
            Análisis por ciudad {analysis.dryRun ? '(sin aplicar)' : ''}
          </Typography>
          {Object.keys(analysis.byCity).map((cityId) => {
            const city = analysis.byCity[cityId];
            return (
              <C2TableCard key={cityId} sx={{ mb: 2, p: 1.5 }}>
                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, color: C2.sidebar, mb: 1 }}>
                  {city.cityName || cityId}
                </Typography>
                <Grid container spacing={1.5}>
                  {Object.keys(city.carTypes || {}).map((id) => {
                    const ct = city.carTypes[id];
                    return (
                      <Grid item xs={12} md={6} key={id}>
                        <Box sx={{ border: `1px solid ${C2.border}`, borderRadius: 2, p: 1.25 }}>
                          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700 }}>
                            {ct.name}{' '}
                            <Chip size="small" label={`${Math.round((ct.confidence || 0) * 100)}%`} />
                          </Typography>
                          {ct.vsMarket ? (
                            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.violet }}>
                              vs mercado: {ct.vsMarket}
                            </Typography>
                          ) : null}
                          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem' }}>
                            Antes: {formatRates(ct.previous)}
                          </Typography>
                          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.85rem', color: C2.violet }}>
                            Sugerido: {formatRates(ct.applied)}
                          </Typography>
                          {ct.rationale ? (
                            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', color: C2.muted, mt: 0.5 }}>
                              {ct.rationale}
                            </Typography>
                          ) : null}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </C2TableCard>
            );
          })}
        </>
      ) : null}

      {lastApplied ? (
        <>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, color: C2.sidebar, mb: 1 }}>
            Última activación (esta sesión)
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {Object.keys(lastApplied).map((id) => {
              const ct = lastApplied[id];
              return (
                <Grid item xs={12} md={6} key={id}>
                  <C2TableCard sx={{ p: 1.5 }}>
                    <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, color: C2.sidebar }}>
                      {ct.name}
                      <Chip size="small" label={`Conf. ${Math.round((ct.confidence || 0) * 100)}%`} sx={{ ml: 1 }} />
                    </Typography>
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.82rem', mt: 0.5 }}>
                      Antes: {formatRates(ct.previous)}
                    </Typography>
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.85rem', color: C2.violet }}>
                      Activo: {formatRates(ct.applied)}
                    </Typography>
                    {ct.rationale ? (
                      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted, mt: 0.5 }}>
                        {ct.rationale}
                      </Typography>
                    ) : null}
                  </C2TableCard>
                </Grid>
              );
            })}
          </Grid>
        </>
      ) : null}

      {history.length > 0 ? (
        <>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, color: C2.sidebar, mb: 1 }}>
            Historial de activaciones
          </Typography>
          {history.slice(0, 10).map((rec) => (
            <Typography
              key={rec.runId || rec.cityId || rec.createdAt}
              sx={{ fontFamily: C2_FONT, fontSize: '0.85rem', color: C2.muted, mb: 0.5 }}
            >
              {rec.runId || rec.cityId} —{' '}
              {Object.keys(rec.carTypes || {}).length} vehículos
              {rec.citiesSynced != null ? ` · ${rec.citiesSynced} ciudades` : ''}
              {' · '}
              {rec.appliedAt || rec.createdAt
                ? new Date(rec.appliedAt || rec.createdAt).toLocaleString('es-CO')
                : ''}
            </Typography>
          ))}
        </>
      ) : null}
    </Box>
  );
}
